import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateDistance, getActivityIcon } from "@/lib/utils";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Search } from "lucide-react";
import type { ActivityLog as ActivityLogType } from "@shared/schema";

// Define interface that matches the API response format
interface ActivityLog extends Omit<ActivityLogType, 'timestamp'> {
  timestamp: string; // The API returns timestamp as string, not Date
}

function ActivityItem({ activity }: { activity: ActivityLog }) {
  const { icon, color } = getActivityIcon(activity.type);
  const timeAgo = formatDateDistance(activity.timestamp);
  
  return (
    <div className="flex items-start py-4 border-b border-slate-100">
      <div className={`w-10 h-10 rounded-full ${color} flex items-center justify-center text-white flex-shrink-0`}>
        <i className={icon}></i>
      </div>
      <div className="ml-4 overflow-hidden">
        <p className="text-sm text-slate-800 break-words">{activity.description}</p>
        <p className="text-xs text-slate-500 mt-1">{timeAgo}</p>
      </div>
    </div>
  );
}

export default function ActivityPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState<string>("all");
  
  const { data, isLoading, error } = useQuery<ActivityLog[]>({
    queryKey: ['/api/activity-logs'],
    placeholderData: [],
  });

  const activities = data || [];
  
  // Filter activities based on search and type filter
  const filteredActivities = activities.filter(activity => {
    const matchesSearch = activity.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === "all" || activity.type === filterType;
    return matchesSearch && matchesType;
  });
  
  // Extract unique activity types for filter options
  const activityTypes = Array.from(new Set(activities.map(activity => activity.type)));

  return (
    <div className="space-y-6 overflow-y-auto max-h-full">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div className="mb-4 md:mb-0">
            <h2 className="text-2xl font-bold text-slate-800">Activity Log</h2>
            <p className="text-sm text-slate-500 mt-1">View a complete history of system activities</p>
          </div>
        </div>
      </div>
      
      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Label htmlFor="search" className="text-xs font-medium mb-2">Search</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
                <Input
                  id="search"
                  placeholder="Search activity descriptions..."
                  className="pl-9"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="type-filter" className="text-xs font-medium mb-2">Filter by Type</Label>
              <Select
                value={filterType}
                onValueChange={setFilterType}
              >
                <SelectTrigger id="type-filter" className="w-full">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="all">All Types</SelectItem>
                    {activityTypes.map(type => (
                      <SelectItem key={type} value={type}>
                        {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Activity List */}
      <Card>
        <CardHeader className="border-b border-slate-200">
          <CardTitle className="text-lg font-semibold">System Activity</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-1">
            {isLoading ? (
              [...Array(8)].map((_, index) => (
                <div key={index} className="flex items-start py-4 border-b border-slate-100">
                  <Skeleton className="w-10 h-10 rounded-full flex-shrink-0" />
                  <div className="ml-4 flex-1 overflow-hidden">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-3 w-2/3 mt-1" />
                  </div>
                </div>
              ))
            ) : error ? (
              <div className="py-8 text-center text-slate-500">
                <p>Error loading activity logs. Please try again later.</p>
              </div>
            ) : filteredActivities.length === 0 ? (
              <div className="py-8 text-center text-slate-500">
                <p>No activities match your search criteria.</p>
              </div>
            ) : (
              filteredActivities.map((activity) => (
                <ActivityItem key={activity.id} activity={activity} />
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}